import { Link, Navigate } from "react-router-dom";
import { ExternalLink, LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../lib/auth";
import { LogoMark } from "../components/Logo";
import { CrudTab } from "../components/admin/CrudTab";
import { ConcertForm } from "../components/admin/ConcertForm";
import { SponsorForm } from "../components/admin/SponsorForm";
import { PackageForm } from "../components/admin/PackageForm";
import { SubscribersTab } from "../components/admin/SubscribersTab";
import { AccountTab } from "../components/admin/AccountTab";
import { cad, fullDate } from "../lib/format";

const TABS = [
  { v: "concerts", label: "Concerts", props: { name: "Concert", listPath: "/concerts", basePath: "/admin/concerts", idKey: "slug", Form: ConcertForm, testPrefix: "concerts",
    columns: { image: (c) => c.image, title: (c) => `${c.artist}${c.is_sample ? " (sample)" : ""}`, sub: (c) => `${fullDate(c.date)} · ${c.status}${c.is_past ? " · Past" : ""}` } } },
  { v: "sponsors", label: "Sponsors", props: { name: "Sponsor", listPath: "/admin/sponsors", basePath: "/admin/sponsors", idKey: "key", Form: SponsorForm, testPrefix: "sponsors",
    columns: { image: (s) => s.logo, title: (s) => s.name, sub: (s) => `${s.tier} · ${s.confirmed ? "Confirmed (visible)" : "Not confirmed (hidden)"}` } } },
  { v: "packages", label: "Packages", props: { name: "Package", listPath: "/admin/packages", basePath: "/admin/packages", idKey: "key", Form: PackageForm, testPrefix: "packages",
    columns: { image: (p) => p.image, title: (p) => p.name, sub: (p) => `${cad(p.price_from) ? `From ${cad(p.price_from)}` : "Pricing coming soon"} · ${p.active ? "Visible" : "Hidden"}` } } },
];

export default function Admin() {
  const { user, logout } = useAuth();
  if (user === null) return <main className="min-h-screen bg-[#0B0C10]" data-testid="admin-loading" />;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <main className="min-h-screen bg-[#0B0C10]" data-testid="admin-dashboard">
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-5">
          <div className="flex items-center gap-3"><LogoMark className="h-8 w-8" /><span className="font-display text-xl font-extrabold uppercase text-white">Admin</span></div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-view-site" className="btn-ghost"><ExternalLink size={14} /> <span className="hidden sm:inline">View site</span></Link>
            <button data-testid="admin-logout-btn" onClick={logout} className="btn-ghost"><LogOut size={14} /> <span className="hidden sm:inline">Log out</span></button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-5xl px-5 py-10">
        <p className="mb-6 text-sm text-zinc-400">Signed in as {user.email}</p>
        <Tabs defaultValue="concerts">
          <TabsList className="mb-6 h-auto flex-wrap gap-1 bg-[#12141C] p-1">
            {[...TABS, { v: "subscribers", label: "Subscribers" }, { v: "account", label: "Account" }].map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`admin-tab-${t.v}`} className="rounded-md px-4 py-2 font-mono text-xs uppercase tracking-widest text-zinc-300 data-[state=active]:bg-[#FF5A1E] data-[state=active]:text-black">{t.label}</TabsTrigger>
            ))}
          </TabsList>
          {TABS.map((t) => <TabsContent key={t.v} value={t.v}><CrudTab {...t.props} /></TabsContent>)}
          <TabsContent value="subscribers"><SubscribersTab /></TabsContent>
          <TabsContent value="account"><AccountTab /></TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
